import { useEffect, useRef } from 'react';
import { PersonStanding, X, RotateCcw, UnfoldVertical, Contrast, Type, AudioLines, ALargeSmall, type LucideIcon } from 'lucide-react';
import { useA11y, setA11y, resetA11y, useA11yMenu, setA11yMenu, canReadAloud, A11Y_DEFAULTS, type TextSize } from '../lib/a11y';

// The header button. Same look as the sound and theme buttons next to it.
export function AccessibilityButton({ className }: { className: string }) {
  const open = useA11yMenu();
  return (
    <button
      type="button"
      aria-label="Accessibility settings"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={(e) => setA11yMenu(!open, e.currentTarget)}
      className={className}
    >
      <PersonStanding size={18} />
    </button>
  );
}

// Option is the Mac's Alt key, so the same shortcut works; the label says which key.
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const SIZES: { value: TextSize; label: string; px: number }[] = [
  { value: 0, label: 'Default', px: 14 },
  { value: 1, label: 'Large', px: 16 },
  { value: 2, label: 'Larger', px: 18 },
  { value: 3, label: 'Largest', px: 20 },
];

// One on/off row. A real switch for screen readers (role="switch").
function Toggle({ on, label, hint, onChange, disabled, isNight, icon: Icon }: { on: boolean; label: string; hint: string; onChange: (v: boolean) => void; disabled?: boolean; isNight: boolean; icon: LucideIcon }) {
  const muted = isNight ? 'text-white/60' : 'text-black/55';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${isNight ? 'hover:bg-white/5' : 'hover:bg-black/[0.03]'}`}
    >
      <span aria-hidden className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isNight ? 'bg-white/5 text-white/70' : 'bg-black/[0.04] text-black/60'}`}>
        <Icon size={16} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        <span className={`block text-xs ${muted}`}>{hint}</span>
      </span>
      <span aria-hidden className={`relative shrink-0 w-10 h-6 rounded-full transition-colors ${on ? 'bg-[#3797F0]' : isNight ? 'bg-white/15' : 'bg-black/15'}`}>
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'left-5' : 'left-1'}`} />
      </span>
    </button>
  );
}

// One small panel for the whole app (opened from any header, or Alt + A).
export function AccessibilityPanel({ isNight, onReadAloudChange }: { isNight: boolean; onReadAloudChange?: (on: boolean) => void }) {
  const open = useA11yMenu();
  const s = useA11y();
  const panelRef = useRef<HTMLDivElement>(null);
  const speech = canReadAloud();
  const changed = JSON.stringify(s) !== JSON.stringify(A11Y_DEFAULTS);

  // Alt + A opens or closes it from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // On a Mac, Option + A types "å": leave it alone while someone is typing.
      const typing = (e.target as HTMLElement | null)?.closest?.('input, textarea, [contenteditable="true"]');
      if (IS_MAC && typing) return;
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyA') {
        e.preventDefault();
        setA11yMenu(!open, open ? null : (document.activeElement as HTMLElement | null));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // While open: focus the first control, keep Tab inside, Escape or a click outside closes.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    (panel?.querySelector<HTMLElement>('button[aria-pressed="true"]') ?? panel?.querySelector<HTMLElement>('button'))?.focus();
    const focusables = () => [...(panel?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])];
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setA11yMenu(false); return; }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) return;
      const i = list.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); list[list.length - 1].focus(); }
      else if (!e.shiftKey && i === list.length - 1) { e.preventDefault(); list[0].focus(); }
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (!panel?.contains(t) && !t.closest('[aria-label="Accessibility settings"]')) setA11yMenu(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown); };
  }, [open]);

  if (!open) return null;

  const muted = isNight ? 'text-white/60' : 'text-black/55';

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Accessibility settings"
      className={`fixed z-[80] top-[4.75rem] right-4 sm:right-8 w-[min(20rem,calc(100vw-2rem))] max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-2xl border p-3 shadow-2xl ${
        isNight ? 'bg-[#141427]/95 border-white/10 text-white backdrop-blur-xl' : 'bg-white border-black/10 text-black'
      }`}
    >
      <div className="flex items-center justify-between px-1 pb-2">
        <h2 className="text-sm font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>Accessibility</h2>
        <button type="button" onClick={() => setA11yMenu(false)} aria-label="Close accessibility settings"
          className={`p-1.5 rounded-full ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}>
          <X size={16} />
        </button>
      </div>

      <div className="px-1 pb-2">
        <p className={`flex items-center gap-1.5 text-xs mb-1.5 ${muted}`} id="a11y-size-label"><ALargeSmall size={14} aria-hidden /> Text size</p>
        <div role="group" aria-labelledby="a11y-size-label" className={`grid grid-cols-4 gap-1 rounded-xl p-1 ${isNight ? 'bg-white/5' : 'bg-black/[0.04]'}`}>
          {SIZES.map(z => (
            <button key={z.value} type="button" aria-pressed={s.textSize === z.value} aria-label={`Text size: ${z.label}`}
              onClick={() => setA11y({ textSize: z.value })}
              className={`h-9 rounded-lg font-semibold transition-colors ${s.textSize === z.value ? (isNight ? 'bg-white/15' : 'bg-white shadow-sm') : ''}`}
              style={{ fontSize: z.px }}>
              A
            </button>
          ))}
        </div>
      </div>

      <Toggle icon={UnfoldVertical} isNight={isNight} on={s.spacing} label="More spacing" hint="Between lines, words and letters" onChange={v => setA11y({ spacing: v })} />
      <Toggle icon={Contrast} isNight={isNight} on={s.contrast} label="High contrast" hint="Stronger text, clearer outlines, underlined links" onChange={v => setA11y({ contrast: v })} />
      <Toggle icon={Type} isNight={isNight} on={s.readableFont} label="Readable font" hint="Letters that are easier to tell apart" onChange={v => setA11y({ readableFont: v })} />
      <Toggle icon={AudioLines} isNight={isNight} on={s.readAloud && speech} label="Read messages aloud" disabled={!speech}
        hint={speech ? 'New chat messages, with a voice on your device' : 'Not available in this browser'}
        onChange={v => { setA11y({ readAloud: v }); onReadAloudChange?.(v); }} />

      <div className={`flex items-center justify-between gap-2 px-1 pt-2 mt-1 border-t text-xs ${isNight ? 'border-white/10' : 'border-black/10'}`}>
        <span className={muted}>Saved on this device only · {IS_MAC ? 'Option' : 'Alt'} + A</span>
        {changed && (
          <button type="button" onClick={() => { const wasReading = s.readAloud; resetA11y(); if (wasReading) onReadAloudChange?.(false); }} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}>
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>
    </div>
  );
}
